import {
  EMPTY_STATEMENT,
  BIOTOPAUSGLEICH_KEYS,
  biotopausgleichKeys,
  RequestStatementSchema,
  type BiotopausgleichKey,
  type RequestStatement,
} from "@/lib/requests/axis-hints";

/**
 * The funnel's own reading of the customer's statement (ARCH-039 WP1).
 *
 * Biotopausgleich is ONE need the customer states in ONE denomination — Ökopunkte or Hektar — so
 * the funnel asks for one and refuses both. That is a refinement of the shared statement, never a
 * replacement: `StatementObjectSchema`, the database CHECK and Sales' own edits stay free, because
 * a single obligation can genuinely be split across both denominations once Sales has spoken to
 * the customer. Only what the customer states about themselves is narrowed here.
 */

export const STATEMENT_DENOMINATION_CONFLICT_CODE = "STATEMENT_DENOMINATION_CONFLICT";
export const STATEMENT_DENOMINATION_CONFLICT_MESSAGE =
  "Biotopausgleich wird entweder in Ökopunkten oder als Ausgleichsfläche in Hektar angegeben. Bitte wählen Sie eine Einheit.";

/** Both denominations of the one Biotopausgleich need selected — what the funnel cannot state. */
export function namesBothDenominations(statement?: RequestStatement | null): boolean {
  return !!statement && BIOTOPAUSGLEICH_KEYS.every((key) => statement[key].selected);
}

/** The shared statement plus „Ökopunkte XOR Hektar": what a funnel submission may carry. */
export const FunnelStatementSchema = RequestStatementSchema.refine(
  (statement) => !namesBothDenominations(statement),
  {
    message: STATEMENT_DENOMINATION_CONFLICT_MESSAGE,
    path: ["naturschutz_area"],
  }
);

/**
 * Whether a submitted value breaks the funnel's rule and nothing else. A statement the shared
 * schema already rejects is a shape problem, reported where every writer's shape is checked; only
 * the refinement is this module's to refuse, so the caller's message is never the wrong one.
 */
export function violatesFunnelStatement(axisHints: unknown): boolean {
  return (
    !FunnelStatementSchema.safeParse(axisHints).success &&
    RequestStatementSchema.safeParse(axisHints).success
  );
}

/**
 * The customer's Biotopausgleich as the funnel reads their statement: the denomination it selects
 * (Ökopunkte before hectares), its unit, the market's product it trades as and how much. None
 * while the statement names no Biotopausgleich. Its ceiling and instrument are not the
 * statement's: they go on the line it becomes.
 */
export interface FunnelBiotopausgleich {
  readonly key: BiotopausgleichKey;
  readonly unit: "oekopunkte" | "ha";
  readonly product: "okopunkte" | "ausgleichsflaechen";
  readonly quantity: number | null;
}

export function funnelBiotopausgleich(
  statement: RequestStatement | null | undefined
): FunnelBiotopausgleich | null {
  const [key] = biotopausgleichKeys(statement);
  if (key === undefined || !statement) return null;
  const quantity = statement[key].quantity ?? null;
  return key === "naturschutz_points"
    ? { key, unit: "oekopunkte", product: "okopunkte", quantity }
    : { key, unit: "ha", product: "ausgleichsflaechen", quantity };
}

/**
 * Narrow a statement to the funnel's reading. Only a session stored before the denomination
 * toggle existed can hold both, and the editor shows both rows so nothing is hidden; the payload
 * keeps the denomination the customer's Biotopausgleich is read from (Ökopunkte), so the request
 * the customer sees confirmed is the request that is stored.
 */
export function toFunnelStatement(statement: RequestStatement | null): RequestStatement | null {
  if (!namesBothDenominations(statement) || !statement) return statement;
  return { ...statement, naturschutz_area: EMPTY_STATEMENT.naturschutz_area };
}
